import {Container, Graphics} from "pixi.js";
import {DrawFrmThing} from "./DrawFrmThing";

// FrmSceneLayout is a flex-box style layout engine for 2d scenes.
//
// Unlike DOM flexbox, the things being laid out are pixi Containers positioned
// in the shared scene coordinate space. The engine measures each node, divides
// up the available rectangle the way flexbox would (main-axis distribution +
// grow, cross-axis alignment), and then repositions/rescales each graphic so it
// lands in its allocated slot.
//
// Node kinds:
//   - row / column: containers that arrange their children along an axis.
//   - group:        one or more graphics treated as a single rigid unit. Their
//                   positions/scales relative to one another (typically set up
//                   via FrmRefPoint linking) are preserved; the whole group is
//                   uniformly scaled + translated to fit its slot.
//
// A bare DrawFrmThing handed to row()/column() is coerced into a group of one.
//
// grow(n):  the node expands to share leftover main-axis space (like flex-grow).
//           A grown group auto-scales its contents to fit 90% of the space it's
//           given, leaving a little breathing room.
// justifyContent / alignItems: main- and cross-axis placement, same names as css.

type Justify = "flex-start" | "center" | "flex-end" | "space-between" | "space-around";
type Align = "flex-start" | "center" | "flex-end";

export type LayoutRect = {x: number; y: number; width: number; height: number};

type LayoutChild = FrmSceneLayout | DrawFrmThing;
type NodeKind = "row" | "column" | "group" | "spacer" | "absolute";

// fraction of a grown node's slot that its contents are scaled to fill.
const GROW_FILL = 0.9;

// dashed bounding-box outline (.dashed()). lengths are in scene units, matching
// the rects the layout works in.
const DASH_LENGTH = 10;
const DASH_GAP = 6;
const DASH_STROKE_WIDTH = 1;
const DASH_COLOR = 0x999999;

type Falsy = 0 | false | null | undefined;

export class FrmSceneLayout {

    private kind: NodeKind;
    private children: FrmSceneLayout[] = [];
    private items: (DrawFrmThing | Falsy)[] = [];

    private growFactor = 0;
    private justify: Justify = "flex-start";
    private align: Align = "center";
    private gapValue = 0;

    private padTopValue = 0;
    private padBottomValue = 0;
    private padLeftValue = 0;
    private padRightValue = 0;

    private spacerWidth = 0;
    private spacerHeight = 0;

    private absoluteTop = 0;
    private absoluteRight = 0;

    private dashedValue = false;
    private dashedGraphic: Graphics | null = null;

    // the fit scale last applied to this group's items by applyGroup, or null if
    // the group isn't grown (so it renders at natural scale and never shrinks its
    // contents). read via minAppliedScale() to size scale-dependent details.
    private lastGroupScale: number | null = null;

    private constructor(kind: NodeKind) {
        this.kind = kind;
    }

    // ---- factories ---------------------------------------------------------

    static row(...children: LayoutChild[]): FrmSceneLayout {
        return FrmSceneLayout.container("row", children);
    }

    static column(...children: (LayoutChild|Falsy)[]): FrmSceneLayout {
        return FrmSceneLayout.container("column", children);
    }

    static group(items: (DrawFrmThing|Falsy)[]): FrmSceneLayout {
        const node = new FrmSceneLayout("group");
        node.items = items;
        return node;
    }

    // position a single item (or sub-layout) at a fixed offset from the top and
    // right edges of the container it's placed in, outside the normal flex flow.
    // It takes no main-axis space and is ignored by its siblings' layout — it
    // floats over the parent's box like css `position: absolute`.
    static absolute(item: LayoutChild, position: {top: number; right: number}): FrmSceneLayout {
        const node = new FrmSceneLayout("absolute");
        node.children = [item instanceof FrmSceneLayout ? item : FrmSceneLayout.group([item])];
        node.absoluteTop = position.top;
        node.absoluteRight = position.right;
        return node;
    }

    // a fixed-size empty node, useful for adding padding/spacing between
    // siblings. Add .grow() to make it a flexible spacer instead.
    static spacing(width: number, height: number): FrmSceneLayout {
        const node = new FrmSceneLayout("spacer");
        node.spacerWidth = width;
        node.spacerHeight = height;
        return node;
    }

    private static container(kind: NodeKind, children: (LayoutChild|Falsy)[]): FrmSceneLayout {
        const node = new FrmSceneLayout(kind);
        node.children = children.map(c => c instanceof FrmSceneLayout ? c : FrmSceneLayout.group([c]));
        return node;
    }

    // ---- builder methods ---------------------------------------------------

    grow(factor: number): this {
        this.growFactor = factor;
        return this;
    }

    // draw a dashed rectangle around this node's allocated box (its bounding box
    // within the layout). Handy for visualizing how the flex layout divides up
    // space. The outline lives in scene space alongside the items it surrounds.
    dashed(): this {
        this.dashedValue = true;
        return this;
    }

    justifyContent(value: Justify): this {
        this.justify = value;
        return this;
    }

    alignItems(value: Align): this {
        this.align = value;
        return this;
    }

    // fixed spacing inserted between children along the main axis (like css gap).
    gap(value: number): this {
        this.gapValue = value;
        return this;
    }

    // inner spacing between this node's edges and its contents (in scene units).
    padding(value: number): this {
        this.padTopValue = value;
        this.padBottomValue = value;
        this.padLeftValue = value;
        this.padRightValue = value;
        return this;
    }

    padTop(value: number): this {
        this.padTopValue = value;
        return this;
    }

    padBottom(value: number): this {
        this.padBottomValue = value;
        return this;
    }

    padLeft(value: number): this {
        this.padLeftValue = value;
        return this;
    }

    padRight(value: number): this {
        this.padRightValue = value;
        return this;
    }

    // every DrawFrmThing positioned by this node and its subtree. Used to wire
    // up change listeners so the layout can re-flow when any child's bounding
    // box changes.
    collectItems(): DrawFrmThing[] {
        const out: DrawFrmThing[] = [];
        this.collectItemsInto(out);
        return out;
    }

    private collectItemsInto(out: DrawFrmThing[]) {
        for(const item of this.items) {
            if(!item) continue;
            out.push(item);
        }

        for(const child of this.children) {
            child.collectItemsInto(out);
        }
    }

    // the smallest fit scale applied to any grown group in this subtree the last
    // time apply() ran (Infinity if no group was shrunk). This is the worst-case
    // shrink, so scale-dependent detail (e.g. dimension stroke width) sized
    // against it stays legible across every group. Call after apply().
    minAppliedScale(): number {
        let min = this.lastGroupScale ?? Infinity;
        for(const child of this.children) {
            min = Math.min(min, child.minAppliedScale());
        }
        return min;
    }

    // ---- layout ------------------------------------------------------------

    // arrange this node (and its subtree) to fill `rect`, in scene coordinates.
    // padding insets the rect before the contents are placed.
    apply(rect: LayoutRect) {
        const inner: LayoutRect = {
            x: rect.x + this.padLeftValue,
            y: rect.y + this.padTopValue,
            width: Math.max(0, rect.width - this.padLeftValue - this.padRightValue),
            height: Math.max(0, rect.height - this.padTopValue - this.padBottomValue),
        };

        if(this.kind === "group") {
            this.applyGroup(inner);
        } else if(this.kind === "spacer") {
            // empty: occupies space but has nothing to position.
        } else if(this.kind === "absolute") {
            this.applyAbsolute(inner);
        } else {
            this.applyContainer(inner);
        }

        // outline the node's full slot (padding included), once its contents are
        // placed so the items it anchors to already have a scene parent.
        if(this.dashedValue) {
            this.applyDashed(rect);
        }
    }

    // lazily create the dashed outline graphic, parent it into the same scene
    // container as the items, and (re)draw it for the current rect. Re-applied on
    // every re-flow, so it always tracks the latest layout.
    private applyDashed(rect: LayoutRect) {
        if(!this.dashedGraphic) {
            const parent = this.findSceneContainer();
            if(!parent) {
                // nothing positioned yet — try again on the next re-flow.
                return;
            }

            const g = new Graphics();
            parent.addChild(g);
            this.dashedGraphic = g;

            // FrmSceneLayout has no teardown of its own, so tie the outline's
            // lifetime to one of the items it surrounds: when the scene destroys
            // that item, the outline goes with it.
            const anchor = this.collectItems()[0];
            anchor?.track({
                destroy: () => {
                    g.destroy();
                    this.dashedGraphic = null;
                }
            });
        }

        this.drawDashedRect(this.dashedGraphic, rect);
    }

    // the pixi container the items are parented to (the shared scene). Returns
    // null until at least one item has been added to the scene.
    private findSceneContainer(): Container | null {
        for(const item of this.collectItems()) {
            if(item.graphic.parent) {
                return item.graphic.parent;
            }
        }

        return null;
    }

    private drawDashedRect(g: Graphics, rect: LayoutRect) {
        g.clear();
        g.lineStyle(DASH_STROKE_WIDTH, DASH_COLOR, 1);

        const {x, y, width, height} = rect;
        const corners = [
            [x, y],
            [x + width, y],
            [x + width, y + height],
            [x, y + height],
        ];

        for(let i = 0; i < corners.length; i++) {
            const [ax, ay] = corners[i];
            const [bx, by] = corners[(i + 1) % corners.length];
            this.dashEdge(g, ax, ay, bx, by);
        }
    }

    // stroke a single edge as a run of dashes.
    private dashEdge(g: Graphics, ax: number, ay: number, bx: number, by: number) {
        const dx = bx - ax;
        const dy = by - ay;
        const len = Math.hypot(dx, dy);
        if(len === 0) {
            return;
        }

        const ux = dx / len;
        const uy = dy / len;
        const step = DASH_LENGTH + DASH_GAP;

        for(let d = 0; d < len; d += step) {
            const end = Math.min(d + DASH_LENGTH, len);
            g.moveTo(ax + ux * d, ay + uy * d);
            g.lineTo(ax + ux * end, ay + uy * end);
        }
    }

    // natural (content) size of this node, padding included, before grow/fit.
    private measure(): {width: number; height: number} {
        const padX = this.padLeftValue + this.padRightValue;
        const padY = this.padTopValue + this.padBottomValue;

        if(this.kind === "group") {
            const b = this.groupBounds();
            return {width: b.width + padX, height: b.height + padY};
        }

        if(this.kind === "spacer") {
            return {width: this.spacerWidth + padX, height: this.spacerHeight + padY};
        }

        const horizontal = this.kind === "row";
        // absolute children float outside the flow and don't contribute size.
        const flow = this.children.filter(c => c.kind !== "absolute");
        const count = flow.length;
        let main = count > 1 ? this.gapValue * (count - 1) : 0;
        let cross = 0;

        for(const child of flow) {
            const size = child.measure();
            const childMain = horizontal ? size.width : size.height;
            const childCross = horizontal ? size.height : size.width;
            main += childMain;
            cross = Math.max(cross, childCross);
        }

        return horizontal
            ? {width: main + padX, height: cross + padY}
            : {width: cross + padX, height: main + padY};
    }

    private applyContainer(rect: LayoutRect) {
        const horizontal = this.kind === "row";
        const containerMain = horizontal ? rect.width : rect.height;

        // absolute children are positioned against the container's box rather
        // than flowed alongside their siblings.
        const flow = this.children.filter(c => c.kind !== "absolute");

        const sizes = flow.map(c => c.measure());
        const childMain = (i: number) => horizontal ? sizes[i].width : sizes[i].height;

        const count = flow.length;

        // fixed gap consumes main-axis space between every adjacent pair.
        const totalGap = count > 1 ? this.gapValue * (count - 1) : 0;

        const totalGrow = flow.reduce((sum, c) => sum + c.growFactor, 0);
        const fixedMain = flow.reduce((sum, c, i) => sum + (c.growFactor > 0 ? 0 : childMain(i)), 0);
        const freeMain = Math.max(0, containerMain - fixedMain - totalGap);

        // main-axis size allotted to each child
        const mainSizes = flow.map((c, i) =>
            c.growFactor > 0
                ? (totalGrow > 0 ? freeMain * c.growFactor / totalGrow : 0)
                : childMain(i));

        const usedMain = mainSizes.reduce((sum, m) => sum + m, 0);
        const leftover = Math.max(0, containerMain - usedMain - totalGap);

        // justify-content distributes leftover slack when nothing is growing.
        // any fixed gap is added on top of the justify spacing.
        let offset = 0;
        let justifyGap = 0;
        if(totalGrow === 0 && count > 0) {
            switch(this.justify) {
                case "center": offset = leftover / 2; break;
                case "flex-end": offset = leftover; break;
                case "space-between": justifyGap = count > 1 ? leftover / (count - 1) : 0; break;
                case "space-around": justifyGap = leftover / count; offset = justifyGap / 2; break;
                default: break; // flex-start
            }
        }

        const gap = this.gapValue + justifyGap;

        let cursor = (horizontal ? rect.x : rect.y) + offset;
        for(let i = 0; i < flow.length; i++) {
            const m = mainSizes[i];
            const childRect: LayoutRect = horizontal
                ? {x: cursor, y: rect.y, width: m, height: rect.height}
                : {x: rect.x, y: cursor, width: rect.width, height: m};

            flow[i].apply(childRect);
            cursor += m + gap;
        }

        // float absolute children over the container's box.
        for(const child of this.children) {
            if(child.kind === "absolute") {
                child.apply(rect);
            }
        }
    }

    private applyGroup(rect: LayoutRect) {
        const b = this.groupBounds();
        if(b.width <= 0 && b.height <= 0) {
            return;
        }

        const bcx = b.x + b.width / 2;
        const bcy = b.y + b.height / 2;

        // grown groups scale to fill most of their slot; others keep natural scale.
        let scale = 1;
        this.lastGroupScale = null;
        if(this.growFactor > 0) {
            const sx = b.width > 0 ? (rect.width * GROW_FILL) / b.width : Infinity;
            const sy = b.height > 0 ? (rect.height * GROW_FILL) / b.height : Infinity;
            const fit = Math.min(sx, sy);
            if(isFinite(fit) && fit > 0) scale = fit;
            this.lastGroupScale = scale;
        }

        // place the group's center at the slot's center. The per-item transform
        // below is the uniform map  p -> scale * p + d  applied in scene space,
        // which keeps every item's position/scale relative to the others intact.
        const tcx = rect.x + rect.width / 2;
        const tcy = rect.y + rect.height / 2;
        const dx = tcx - scale * bcx;
        const dy = tcy - scale * bcy;

        for(const item of this.items) {
            if(!item) continue;

            const g = item.graphic;
            g.scale.set(g.scale.x * scale, g.scale.y * scale);
            g.position.set(scale * g.position.x + dx, scale * g.position.y + dy);
        }
    }

    // place the single wrapped child at its natural size, offset from the top and
    // right edges of `rect` (the box of the container this node lives in).
    private applyAbsolute(rect: LayoutRect) {
        const child = this.children[0];
        if(!child) {
            return;
        }

        const size = child.measure();
        child.apply({
            x: rect.x + rect.width - this.absoluteRight - size.width,
            y: rect.y + this.absoluteTop,
            width: size.width,
            height: size.height,
        });
    }

    // union of every item's axis-aligned bounds in scene (parent) space. Each
    // item's local bounds are mapped through its own transform so the group's
    // current relative arrangement is reflected.
    private groupBounds(): LayoutRect {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

        for(const item of this.items) {
            if(!item) continue;

            const g = item.graphic;
            const lb = g.getLocalBounds();

            g.transform.updateLocalTransform();
            const m = g.localTransform;

            const corners = [
                [lb.x, lb.y],
                [lb.x + lb.width, lb.y],
                [lb.x, lb.y + lb.height],
                [lb.x + lb.width, lb.y + lb.height],
            ];

            for(const [lx, ly] of corners) {
                const x = m.a * lx + m.c * ly + m.tx;
                const y = m.b * lx + m.d * ly + m.ty;
                if(x < minX) minX = x;
                if(x > maxX) maxX = x;
                if(y < minY) minY = y;
                if(y > maxY) maxY = y;
            }
        }

        if(!isFinite(minX)) {
            return {x: 0, y: 0, width: 0, height: 0};
        }

        return {x: minX, y: minY, width: maxX - minX, height: maxY - minY};
    }
}
